import React, {useEffect, useState} from 'react';
import {useAlert} from 'src/shared/hooks/useAlert';

import {Button} from '@mantine/core';
import {Badge} from 'src/design/atoms/Badge';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Flex} from 'src/design/atoms/Flex';
import {Grid} from 'src/design/atoms/Grid';
import {Loader} from 'src/design/atoms/Loader';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {ThemeIcon} from 'src/design/atoms/ThemeIcon';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {useDebouncedValue} from '@mantine/hooks';
import {
  CloseIcon,
  IconSize,
} from 'src/design/atoms/icons/CommonIcons';
import {PageHeaderSearch} from 'src/design/organisms/PageHeader';
import type {TaskAssignmentSimpleResponse} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  TaskAssignmentStateEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {StatCard} from 'src/design/molecules/StatCard';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {FeatureFlagGuard} from 'src/app/auth/FeatureFlagGuard';
import {
  getTaskTypeColor,
  getTaskTypeIcon,
  isFormSubmissionProcessing,
  PROCESSING_POLL_MS,
  TaskAssignmentRowPills,
  taskAssignmentRowSubText,
} from 'src/app/tasks/taskHelpers';
import {AllClearState} from 'src/design/molecules/AllClearState';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import {isPendingSync} from 'src/offline/pendingSync';
import {useConnectivity} from 'src/offline/useConnectivity';
import {useAppSelector} from 'src/redux/hooks';
import {
  useGetTaskAssignmentsListQuery,
  useGetTaskAssignmentViewQuery,
} from 'src/redux/queries/taskAssignment';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {TaskPreviewDrawer} from '../TaskPreviewDrawer/TaskPreviewDrawer';

const maxOpenItemsRows = 10;

interface OpenTasksFilters {
  taskType: TaskTypeEnum | null;
}

export interface OpenTasksViewProps {
  /** Right-column panel beside the task list, below the full-width stat cards. */
  aside?: React.ReactNode;
}

export const OpenTasksView: React.FC<OpenTasksViewProps> = ({aside}) => {
  const {session} = useAppSelector(state => state.session);
  const userId = session?.user?.id;

  const {setAlert} = useAlert();
  const {isOffline} = useConnectivity();

  const {
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    currentPage,
    handlePageChange,
    start,
    length,
  } = useListState<OpenTasksFilters>({
    identifier: 'dashboard-open-tasks',
    pageSize: maxOpenItemsRows,
    defaultFilters: {taskType: null},
  });
  const filter = filters.taskType;
  const [debouncedSearch] = useDebouncedValue(searchTerm, 300);

  const skipUser = userId === undefined;
  const [isProcessing, setIsProcessing] = useState(false);
  const pollingInterval = isProcessing && !isOffline ? PROCESSING_POLL_MS : 0;

  const {
    data: listData,
    isLoading: isDataLoading,
    error: listError,
  } = useGetTaskAssignmentsListQuery(
    {
      start,
      length,
      assignmentStates: [
        TaskAssignmentStateEnum.Assigned,
        TaskAssignmentStateEnum.Viewed,
      ],
      assignedToUserId: userId!,
      taskType: filter ?? undefined,
      search: debouncedSearch,
    },
    {skip: skipUser, pollingInterval}
  );

  useEffect(() => {
    setIsProcessing((listData?.data ?? []).some(isFormSubmissionProcessing));
  }, [listData]);

  const {
    data: viewCounts,
    isLoading: isStatisticsLoading,
    error: viewError,
  } = useGetTaskAssignmentViewQuery(
    {start: 0, length: 5, assignedToUserId: userId!},
    // The counts are not bundled offline; the list below still serves from cache.
    {skip: skipUser || isOffline, pollingInterval}
  );

  if (listError) {
    setAlert(parseErrorMessage(listError), 'error');
  }
  if (viewError) {
    setAlert(parseErrorMessage(viewError), 'error');
  }

  const openItems: TaskAssignmentSimpleResponse[] = listData?.data ?? [];
  const totalRecords = listData?.totalRecords ?? 0;

  const [previewItem, setPreviewItem] = useState<
    TaskAssignmentSimpleResponse | undefined
  >();
  const [previewOpened, setPreviewOpened] = useState(false);

  const handleOpenPreview = (
    _id: number | string,
    item: TaskAssignmentSimpleResponse
  ) => {
    setPreviewItem(item);
    setPreviewOpened(true);
  };

  const handleFilterClick = (newFilter: TaskTypeEnum) => {
    setFilters({taskType: filter === newFilter ? null : newFilter});
  };

  const getRowIcon = (item: TaskAssignmentSimpleResponse) => {
    const taskType = item.taskDefinition?.taskType;
    const color = getTaskTypeColor(taskType);
    return (
      <ThemeIcon size="md" variant="light" color={color}>
        {getTaskTypeIcon(taskType)}
      </ThemeIcon>
    );
  };

  const getRowText = (item: TaskAssignmentSimpleResponse) => {
    return item.taskDefinition?.subject ?? '';
  };

  const sectionHeaderCount = totalRecords > 0 && (
    <Badge size="lg" variant="light" color="gray" circle>
      {totalRecords}
    </Badge>
  );

  const sectionHeaderRight = (
    <Flex align="center" gap="sm" wrap="wrap" w={{base: '100%', sm: 'auto'}}>
      {filter && (
        <Tooltip label="Clear filter">
          <Button
            size="compact-xs"
            variant="light"
            rightSection={<CloseIcon size={IconSize.sm} />}
            color={getTaskTypeColor(filter)}
            onClick={() => setFilters({taskType: null})}>
            {filter === TaskTypeEnum.ToolboxTalk && 'Toolbox Talks'}
            {filter === TaskTypeEnum.Actions && 'Actions'}
            {filter === TaskTypeEnum.Form && 'Forms'}
            {filter === TaskTypeEnum.Approval && 'Approvals'}
          </Button>
        </Tooltip>
      )}
      <Box flex={{base: '1 1 100%', sm: '0 0 auto'}} w={{base: 'auto', sm: 240}}>
        <PageHeaderSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search tasks..."
          width="100%"
        />
      </Box>
    </Flex>
  );

  const taskList = (
    <SectionCard
      title="Open Tasks"
      titleAdornment={sectionHeaderCount}
      rightSection={sectionHeaderRight}>
      <Box px="sm" py="xs">
        {totalRecords === 0 && !isDataLoading && !searchTerm ? (
          <AllClearState
            message={filter ? 'No tasks in this category' : undefined}
          />
        ) : (
          <DataList
            data={openItems}
            meta={{label: 'subject'}}
            currentPage={currentPage}
            totalRecords={totalRecords}
            recordsPerPage={maxOpenItemsRows}
            isLoading={isDataLoading}
            hasSearchTerm={!!searchTerm}
            paginatorAtBottom
            onRowClick={handleOpenPreview}
            onIsRowDisabled={isPendingSync}
            onPageChange={handlePageChange}
            onGetRowIcon={getRowIcon}
            onRowTextRender={getRowText}
            onRowSubTextRender={taskAssignmentRowSubText}
            onGetPillMapping={TaskAssignmentRowPills}
          />
        )}
      </Box>
    </SectionCard>
  );

  return (
    <Stack gap="lg">
      {/* Statistics Cards */}
      {isStatisticsLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : (
        viewCounts && (
          <SimpleGrid cols={{base: 2, sm: 3, md: 4}} spacing="md">
            <FeatureFlagGuard
              requiredFeatureFlag={FeatureFlagEnum.ToolboxTalks}>
              <StatCard
                title="Toolbox Talks"
                count={viewCounts.toolboxTalkCount}
                icon={getTaskTypeIcon(TaskTypeEnum.ToolboxTalk, 20)}
                color={getTaskTypeColor(TaskTypeEnum.ToolboxTalk)}
                isActive={filter === TaskTypeEnum.ToolboxTalk}
                onClick={() => handleFilterClick(TaskTypeEnum.ToolboxTalk)}
                mode="filter"
                tooltip="Click to filter tasks"
              />
            </FeatureFlagGuard>
            <FeatureFlagGuard requiredFeatureFlag={FeatureFlagEnum.Actions}>
              <StatCard
                title="Actions"
                count={viewCounts.actionCount}
                icon={getTaskTypeIcon(TaskTypeEnum.Actions, 20)}
                color={getTaskTypeColor(TaskTypeEnum.Actions)}
                isActive={filter === TaskTypeEnum.Actions}
                onClick={() => handleFilterClick(TaskTypeEnum.Actions)}
                mode="filter"
                tooltip="Click to filter tasks"
              />
            </FeatureFlagGuard>
            <FeatureFlagGuard requiredFeatureFlag={FeatureFlagEnum.Forms}>
              <StatCard
                title="Assigned Forms"
                count={viewCounts.assignedFormCount}
                icon={getTaskTypeIcon(TaskTypeEnum.Form, 20)}
                color={getTaskTypeColor(TaskTypeEnum.Form)}
                isActive={filter === TaskTypeEnum.Form}
                onClick={() => handleFilterClick(TaskTypeEnum.Form)}
                mode="filter"
                tooltip="Click to filter tasks"
              />
            </FeatureFlagGuard>
            <FeatureFlagGuard requiredFeatureFlag={FeatureFlagEnum.Approvals}>
              <StatCard
                title="Approvals"
                count={viewCounts.approvalCount}
                icon={getTaskTypeIcon(TaskTypeEnum.Approval, 20)}
                color={getTaskTypeColor(TaskTypeEnum.Approval)}
                isActive={filter === TaskTypeEnum.Approval}
                onClick={() => handleFilterClick(TaskTypeEnum.Approval)}
                mode="filter"
                tooltip="Click to filter tasks"
              />
            </FeatureFlagGuard>
          </SimpleGrid>
        )
      )}

      {aside ? (
        <Grid gutter="lg">
          <Grid.Col span={{base: 12, lg: 8}}>{taskList}</Grid.Col>
          <Grid.Col span={{base: 12, lg: 4}}>{aside}</Grid.Col>
        </Grid>
      ) : (
        taskList
      )}

      <TaskPreviewDrawer
        item={previewItem}
        opened={previewOpened}
        onClose={() => setPreviewOpened(false)}
      />
    </Stack>
  );
};
